<template>
  <v-card tile light>
    <v-toolbar
      flat
      max-height="56"
      color="grey lighten-4"
    >
      <v-slide-x-transition>
        <v-btn
          v-show="editForm != 'regular'"
          icon
          color="primary"
          @click="editForm = 'regular'"
        >
          <v-icon v-text="icons.back" />
        </v-btn>
      </v-slide-x-transition>
      <div
        class="d-inline-block text-h6 text-truncate"
        style="position: absolute; width: 72%; transition: left .275s;"
        :style="{'left': editForm == 'regular' ? '24px' : '56px'}"
        v-text="content.name"
      />
      <v-spacer />
      <v-btn
        icon
        color="primary"
        @click.stop="done()"
      >
        <v-icon v-text="icons.done" />
      </v-btn>
    </v-toolbar>
    <v-divider />
    <v-card-text class="px-0">
      <v-window touchless v-model="editForm">
        <v-window-item value="regular">
          <h-regular-form
            :content="content"
            :form="form"
            :canEditMenu="canEditMenu"
            :canEditModifiers="canEditModifiers"
            @input="updateForm"
            @goto="goto"
            @close="close"
          />
        </v-window-item>
        <v-window-item value="dishOrder">
          <h-dish-orders-list
            v-model="form.dishOrder"
            @input="isDirty = true"
          />
        </v-window-item>
        <v-window-item value="discount">
          <h-discounts-list
            :discounts="$store.getters['rummage/itemDiscounts']"
            :selected="form.discount"
            @apply="updateForm({ field: 'discount', value: $event })"
            @cancel="updateForm({ field: 'discount', value: null })"
          />
        </v-window-item>
        <v-window-item v-if="canEditMenu" value="menu" class="px-2">
          <h-menu-header
            @change="isDirty = true"
          />
          <h-menu-items
            @change="isDirty = true"
          />
        </v-window-item>
        <v-window-item v-else-if="canEditModifiers" value="modifiers">
          <h-modifiers
            @change="isDirty = true"
          />
        </v-window-item>
        <!-- This last empty window-item prevents the window animation to be reversed -->
        <v-window-item>-</v-window-item>
      </v-window>
    </v-card-text>
    <v-divider />
    <v-card-actions>
      <v-row no-gutters class="my-2 text-caption">
        <v-col cols="9" class="grey--text text-truncate">
          <v-icon left small v-text="icons.user" />{{ waiter }}
        </v-col>
        <v-col cols="3" class="text-truncate text-right">
          {{ content.total | currency }}
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
  import Currenciable from '@/Mixins/Currenciable'
  import UsesIcons from '@/Mixins/UsesIcons'
  import HRegularForm from './Form'
  import HDishOrdersList from './DishOrders'
  import HDiscountsList from '@/Sections/Order/Dialogs/Discount/List'
  import HMenuHeader from '@/Sections/Order/Menu/Header'
  import HMenuItems from '@/Sections/Order/Menu/Items'
  import HModifiers from '@/Sections/Order/Modifiers/Stepper'

  export default {
    mixins: [
      Currenciable,
      UsesIcons,
    ],

    components: {
      HRegularForm,
      HDishOrdersList,
      HDiscountsList,
      HMenuHeader,
      HMenuItems,
      HModifiers,
    },

    props: {
      content: {
        required: true,
        type: Object,
      },
    },

    data: () => ({
      editForm: undefined,
      isDirty: undefined,
      form: {
        dishOrder: 0,
        notes: null,
        discount: {},
        subContents: [],
        modifiers: [],
      },
    }),

    computed: {
      canEditMenu () {
        return Boolean(this.content.subContents?.length)
      },
      canEditModifiers () {
        // code copied from XEF APP
        const ids = this.$store.getters['catalog/visibleModifierCategories'](this.content.item).pluck('id')
        return ids.length > 0 && ids.length == ids.unique().length
      },
      waiter () {
        return this.$store.getters['rummage/waiter'](this.content.tenantUser_id)
      },
    },

    created () {
      this.init()
    },

    watch: {
      content () {
        this.init()
      },
    },

    methods: {
      reset () {
        this.editForm = 'regular'
        this.isDirty = false
      },
      init () {
        this.reset()
        this.form.dishOrder = this.content.dishOrder
        this.form.notes = this.content.notes
        this.form.discount = this.content.discount
        this.form.subContents = []
        this.form.modifiers = []
        if (this.canEditMenu) {
          this.$store.dispatch('orders/menu/init', {
            item: this.content.item,
            quantity: this.content.quantity,
            editing: true,
            menu: this.content.subContents,
          })
        } else if (this.canEditModifiers) {
          this.$store.dispatch('orders/modifiers/init', {
            item: this.content.item,
            editing: true,
            modifiers: this.content.modifiers,
          })
        }
      },
      goto (v) {
        this.editForm = v
      },
      updateForm ({ field, value }) {
        this.form[field] = value
        this.isDirty = true
      },
      done () {
        if (this.isDirty) {
          this.update()
          return
        }
        this.close()
      },
      update () {
        if (this.canEditMenu) {
          this.updateContentWithMenu()
          return
        }
        if (this.canEditModifiers) {
          this.updateContentWithModifiers()
          return
        }
        this.updateContent()
      },
      async updateContentWithMenu () {
        const valid = await this.$store.dispatch('orders/menu/validate')
        if (! valid) {
          this.editForm = 'menu'
          return
        }
        this.form.subContents = [...this.$store.state.orders.menu.contents]
        this.form.quantity = this.$store.state.orders.menu.quantity
        this.updateContent()
      },
      updateContentWithModifiers () {
        this.form.modifiers = [
          ...this.$store.getters['orders/modifiers/selected'],
          ...this.$store.getters['orders/modifiers/hidden'](this.content.item),
        ]
        this.updateContent()
      },
      updateContent () {
        this.$store.commit('orders/updateContent', {
          index: this.content.index,
          attributes: this.form,
        })
        this.close()
      },
      close () {
        this.$store.dispatch('orders/dialogs/close')
        setTimeout(() => this.reset(), 150)
      },
    },
  }
</script>
